/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useGeofenceContext } from "@src/appV2/Location/geofence/useGeofence";
import { useWorkerEtaImprovementsConfig } from "@src/appV2/redesign/ClipboardScore/hooks/useWorkerEtaImprovementsConfig";
import { getClockInTimingInfo } from "@src/appV2/redesign/utils/clockInTiming";
import { isAtLeast12HoursAway } from "@src/appV2/redesign/utils/isAtLeast12HoursAway";
import { useArrivalState } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/ParkingCard";
import { useTravelTime } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/ParkingCard/hooks/useTravelTime";
import { type ShiftStateData } from "@src/appV2/Shifts/Shift/ShiftState/types";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { differenceInHours, differenceInMinutes, isAfter, isEqual, parseISO } from "date-fns";

interface UseShiftActionVisibilityParams {
  shift: WorkerShift;
  shiftState: ShiftStateData;
  currentDate: Date;
  isTestAccount?: boolean;
}

interface ShiftTimeCalculations {
  shiftStartTime: Date | undefined;
  isShiftMoreThan12HoursAway: boolean;
  isShiftMoreThan1HourAway: boolean;
  isShiftMoreThan10MinutesAway: boolean;
  isAfterShiftStart: boolean;
}

/**
 * Helper function to calculate all time-related shift values
 */
function calculateShiftTimes(shift: WorkerShift, currentDate: Date): ShiftTimeCalculations {
  const shiftStartTime = isDefined(shift.start) ? parseISO(shift.start) : undefined;

  const isShiftMoreThan12HoursAway = shiftStartTime
    ? isAtLeast12HoursAway(shiftStartTime, currentDate)
    : false;

  const isShiftMoreThan1HourAway = shiftStartTime
    ? differenceInHours(shiftStartTime, currentDate) >= 1
    : false;

  const isShiftMoreThan10MinutesAway = shiftStartTime
    ? differenceInMinutes(shiftStartTime, currentDate) > 10
    : false;

  const isAfterShiftStart =
    isDefined(shiftStartTime) && isDefined(currentDate)
      ? isAfter(currentDate, shiftStartTime) || isEqual(currentDate, shiftStartTime)
      : false;

  return {
    shiftStartTime,
    isShiftMoreThan12HoursAway,
    isShiftMoreThan1HourAway,
    isShiftMoreThan10MinutesAway,
    isAfterShiftStart,
  };
}

interface ShiftOptionsActions {
  clockIn: { show: boolean; enabled: boolean };
  acceptNonInstantPay: { show: boolean; enabled: boolean };
  updateEta: { show: boolean; enabled: boolean };
  sentHome: { show: boolean; enabled: boolean };
  cancelShift: { show: boolean; enabled: boolean };
  uploadTimesheet?: { show: boolean; enabled: boolean }; // Only for non-IP
  transferShift?: { show: boolean; enabled: boolean }; // Only for IP
  showWorkplaceGuide: boolean;
}

const CLOCK_IN_VISIBILITY_THRESHOLD_DRIVE_TIME_IN_MINUTES = 3;

/**
 * Hook for Footer: Returns the single primary action button that should be shown
 * Priority order (only one will be returned):
 * 1. Transfer shift (IP only, if enabled + >1 hour away)
 * 2. Cancel shift (if >12 hours away)
 * 3. I was sent home (non-IP shifts before start + inside geofence - EXCEPTION CASE)
 * 4. Upload timesheet (non-IP shifts - always show except for the exception above)
 * 5. Clock in now (IP shifts ≤12 hours + inside) or (IP shifts ≤10 minutes + ≤3 minutes drive away)
 * 6. Update ETA (IP shifts ≤12 hours + outside)
 */
// eslint-disable-next-line complexity
export function useFooterPrimaryAction(props: UseShiftActionVisibilityParams) {
  const { shift, shiftState, currentDate } = props;

  const { inside } = useGeofenceContext();
  const { enabled: isWorkerEtaUpdateEnabled } = useWorkerEtaImprovementsConfig();

  const arrivalContext = useArrivalState({
    shiftStart: shift.start,
    currentDate,
  });

  const {
    estimatedTravelTime,
    isLoading: isEstimatedTravelTimeLoading,
    error: estimatedTravelTimeError,
  } = useTravelTime({
    arrivalContext,
    facilityCoordinates: shift.facility.geoLocation?.coordinates,
    workerId: shift.agentId,
    shiftId: shift._id,
    workplaceId: shift.facilityId,
    shiftStartIso: shift.start,
  });

  const isTransferFeatureEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_TRANSFER_ENABLED, {
    defaultValue: false,
  });

  // Use pre-calculated values if provided, otherwise calculate them
  const {
    isShiftMoreThan12HoursAway,
    isShiftMoreThan1HourAway,
    isShiftMoreThan10MinutesAway,
    isAfterShiftStart: isAfterOrEqualShiftStart,
  } = calculateShiftTimes(shift, currentDate);

  // Priority 1: Transfer shift (highest priority, IP only)
  if (shift.isInstantPay && isTransferFeatureEnabled && isShiftMoreThan1HourAway) {
    return {
      action: "transferShift" as const,
      enabled: shift.isTransferrable ?? false,
    };
  }

  // Priority 2: Cancel shift
  if (isShiftMoreThan12HoursAway) {
    return {
      action: "cancelShift" as const,
      enabled: true,
    };
  }

  // Priority 3: Sent home (non-IP shifts before start + inside geofence) - EXCEPTION CASE
  if (!shift.isInstantPay && !isShiftMoreThan12HoursAway && !isAfterOrEqualShiftStart && inside) {
    return {
      action: "sentHome" as const,
      enabled: true,
    };
  }

  // Priority 4: Upload timesheet (non-IP shifts - always show except for the exception above)
  if (!shift.isInstantPay && !isShiftMoreThan12HoursAway) {
    return {
      action: "uploadTimesheet" as const,
      enabled: true,
    };
  }

  // Priority 5: Clock in now (IP shifts within 12 hours + inside) or (IP shifts within 10 minutes + up to 3 minutes drive away)
  const isShortTravelTime =
    !isEstimatedTravelTimeLoading &&
    isDefined(estimatedTravelTime) &&
    estimatedTravelTime <= CLOCK_IN_VISIBILITY_THRESHOLD_DRIVE_TIME_IN_MINUTES;

  const isInsideForPendingShift = !isShiftMoreThan12HoursAway && inside;
  const isShortTravelTimeForPendingClockIn = !isShiftMoreThan10MinutesAway && isShortTravelTime;

  const hasErrorOnEstimatedTravelTime =
    !isShiftMoreThan10MinutesAway && isDefined(estimatedTravelTimeError);

  if (
    shift.isInstantPay &&
    (isInsideForPendingShift || isShortTravelTimeForPendingClockIn || hasErrorOnEstimatedTravelTime)
  ) {
    const { canClockInNow } = getClockInTimingInfo({
      shiftStart: shift.start,
      shiftState,
      currentDate,
    });

    return {
      action: "clockIn" as const,
      enabled: canClockInNow,
    };
  }

  // Priority 6: Update ETA (IP shifts within 12 hours + outside)
  if (shift.isInstantPay && !isShiftMoreThan12HoursAway && !inside && !isWorkerEtaUpdateEnabled) {
    return {
      action: "updateEta" as const,
      enabled: true,
    };
  }

  // No primary action available
  return null;
}

/**
 * Hook for ShiftOptionsBottomSheet: Returns all available options for the bottom sheet
 * This shows multiple actions based on shift type and state, excluding the primary action shown in Footer
 */
export function useShiftOptionsActions({
  shift,
  shiftState,
  currentDate,
  isTestAccount,
}: UseShiftActionVisibilityParams) {
  const { inside } = useGeofenceContext();

  const isTransferFeatureEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_TRANSFER_ENABLED, {
    defaultValue: false,
  });

  const { isShiftMoreThan12HoursAway, isShiftMoreThan1HourAway, isAfterShiftStart } =
    calculateShiftTimes(shift, currentDate);

  const isClockedOut = isDefined(shift.clockInOut?.end);

  const footerPrimaryAction = useFooterPrimaryAction({
    shift,
    shiftState,
    currentDate,
    isTestAccount,
  });

  const isFooterAction = (actionName: string) => footerPrimaryAction?.action === actionName;

  return shift.isInstantPay
    ? getInstantPayActions({
        isShiftMoreThan1HourAway,
        isShiftMoreThan12HoursAway,
        inside,
        isTestAccount,
        isTransferFeatureEnabled,
        isShiftTransferable: shift.isTransferrable ?? false,
        isClockedOut,
        isFooterAction,
      })
    : getNonInstantPayActions({
        isShiftMoreThan12HoursAway,
        inside,
        isAfterShiftStart,
        isClockedOut,
        isFooterAction,
      });
}

function getInstantPayActions({
  isTestAccount,
  isShiftMoreThan1HourAway,
  isShiftMoreThan12HoursAway,
  inside,
  isTransferFeatureEnabled,
  isShiftTransferable,
  isClockedOut,
  isFooterAction,
}: {
  isTestAccount?: boolean;
  isShiftMoreThan1HourAway: boolean;
  isShiftMoreThan12HoursAway: boolean;
  inside: boolean;
  isTransferFeatureEnabled: boolean;
  isShiftTransferable: boolean;
  isClockedOut: boolean;
  isFooterAction: (action: string) => boolean;
}): ShiftOptionsActions {
  return {
    // Instant Pay
    clockIn: {
      show: false, // Clock in should NEVER be in bottom sheet - only in footer
      enabled: false,
    },
    acceptNonInstantPay: {
      show: Boolean(isTestAccount),
      enabled: true,
    },
    updateEta: {
      show: !isShiftMoreThan12HoursAway && !inside && !isClockedOut && !isFooterAction("updateEta"),
      enabled: true,
    },
    sentHome: {
      show: !isShiftMoreThan12HoursAway && inside && !isFooterAction("sentHome"),
      enabled: true,
    },
    cancelShift: {
      show: !isClockedOut,
      enabled: true,
    },
    transferShift: {
      show:
        !isFooterAction("transferShift") && isShiftMoreThan1HourAway && isTransferFeatureEnabled,
      enabled: isShiftTransferable,
    },
    showWorkplaceGuide: true,
  };
}

function getNonInstantPayActions({
  isShiftMoreThan12HoursAway,
  inside,
  isAfterShiftStart,
  isClockedOut,
  isFooterAction,
}: {
  isShiftMoreThan12HoursAway: boolean;
  inside: boolean;
  isAfterShiftStart: boolean;
  isClockedOut: boolean;
  isFooterAction: (action: string) => boolean;
}): ShiftOptionsActions {
  return {
    // Non-Instant Pay
    clockIn: {
      show: false, // Non-IP doesn't have clock in
      enabled: false,
    },
    acceptNonInstantPay: {
      show: false,
      enabled: false,
    },
    updateEta: {
      show:
        !isShiftMoreThan12HoursAway &&
        !inside &&
        !isAfterShiftStart &&
        !isClockedOut &&
        !isFooterAction("updateEta"),
      enabled: true,
    },
    sentHome: {
      show: !isShiftMoreThan12HoursAway && isAfterShiftStart && !isFooterAction("sentHome"),
      enabled: true,
    },
    cancelShift: {
      show: !isClockedOut,
      enabled: true,
    },
    uploadTimesheet: {
      show:
        !isShiftMoreThan12HoursAway &&
        isAfterShiftStart &&
        isClockedOut &&
        !isFooterAction("uploadTimesheet"),
      enabled: true,
    },
    showWorkplaceGuide: false, // Non-IP doesn't show workplace guide
  };
}
/* eslint-enable max-lines */
